/**
 * HustleRoundupSalesPage.jsx — served at `hustleroundup.au/` (bare).
 * Public, no login. Replaces the previous "coming-soon" splash with a
 * proper marketing page that explains the product and points visitors
 * to `app.hustleroundup.au` to actually start a room.
 *
 * HustleRoundup is the HustleSuite platform's Zoom-lookalike video
 * meeting module — live rooms, AI transcripts, digest emails,
 * recordings. Sprint 1/2 backend is live; this is the public front
 * door.
 */
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Video, Sparkles, Mail, Shield, Users, PlayCircle, ArrowRight, Zap, Layers } from "lucide-react";
import PlatformHeader from "@/components/PlatformHeader";
import SuiteFooter from "@/components/SuiteFooter";

const APP_URL = "https://app.hustleroundup.au";

function Bullet({ Icon, iconTitle, title, body }) {
  return (
    <div className="rounded-2xl border border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-900/50 p-6 shadow-sm">
      <span className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-orange-100 dark:bg-orange-500/15 text-orange-700 dark:text-orange-300" title={iconTitle}>
        <Icon size={19} strokeWidth={1.75} aria-label={iconTitle} />
      </span>
      <h3 className="mt-4 font-black text-stone-900 dark:text-stone-50 text-base">{title}</h3>
      <p className="mt-1.5 text-sm text-stone-700 dark:text-stone-300 leading-relaxed">{body}</p>
    </div>
  );
}

export default function HustleRoundupSalesPage() {
  const { t, i18n } = useTranslation();
  useEffect(() => {
    document.documentElement.lang = (i18n.language || "en").split("-")[0];
    document.title = t("roundup_meta_title", {
      defaultValue: "HustleRoundup — Live rooms + AI recap",
    });
  }, [i18n.language, t]);

  return (
    <div
      className="min-h-screen bg-stone-50 text-stone-900 dark:bg-stone-950 dark:text-stone-50"
      data-testid="hustleroundup-sales-page"
    >
      <PlatformHeader salesTopGap
        brand={{ name: "HustleRoundup", to: "/" }}
        nav={[
          { to: "/",                  label: t("roundup_nav_home",  { defaultValue: "Home" }) },
          { to: APP_URL,              label: t("roundup_nav_start", { defaultValue: "Start meeting" }), align: "right", variant: "primary", external: true },
          { to: `${APP_URL}/login`,   label: t("roundup_nav_signin",{ defaultValue: "Sign in" }),      align: "right", external: true },
        ]}
        showBookCta={false}
        showCart={false}
      />

      {/* ── Hero ─────────────────────────────────────────────────── */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-orange-500/10 via-transparent to-purple-500/10 pointer-events-none" />
        <div className="relative max-w-6xl mx-auto px-6 sm:px-10 pt-16 sm:pt-24 pb-14">
          <p className="text-[11px] font-black uppercase tracking-[0.3em] text-orange-700 dark:text-orange-300">
            Part of HustleSuite
          </p>
          <h1
            className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-black leading-[1.05] max-w-4xl text-stone-900 dark:text-stone-50"
            data-testid="roundup-hero-title"
          >
            Live rooms. Real conversations. <span className="text-orange-700 dark:text-orange-400">AI writes the recap</span> so you don&apos;t have to.
          </h1>
          <p className="mt-6 text-base sm:text-lg text-stone-700 dark:text-stone-200 max-w-2xl leading-relaxed">
            HustleRoundup is the HustleSuite team&apos;s built-in video meeting room —
            one-tap start, guest-friendly links, full-cloud recording, and an
            AI-generated transcript + digest email in your inbox the moment the
            call wraps. Uses the same login as every other Hustle module.
          </p>

          <div className="mt-8 flex flex-wrap gap-3">
            <a
              href={APP_URL}
              data-testid="cta-start-meeting"
              rel="noopener"
              className="inline-flex items-center gap-2 rounded-full bg-orange-600 hover:bg-orange-700 text-white font-black px-6 h-12 transition-colors"
              title="Start a new meeting on app.hustleroundup.au"
            >
              <Video size={17} aria-hidden="true" /> Start a meeting
            </a>
            <a
              href={`${APP_URL}/signup`}
              data-testid="cta-join-membership"
              rel="noopener"
              className="inline-flex items-center gap-2 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white font-black px-6 h-12 transition-colors"
              title="Join Hustle Membership — free"
            >
              Join Hustle Membership
            </a>
            <a
              href={`${APP_URL}/login`}
              data-testid="cta-login"
              rel="noopener"
              className="inline-flex items-center gap-2 rounded-full border-2 border-stone-900 dark:border-stone-100 text-stone-900 dark:text-stone-100 hover:bg-stone-900 hover:text-white dark:hover:bg-stone-100 dark:hover:text-stone-900 font-black px-6 h-12 transition-colors"
              title="Sign in with your HustleSuite account"
            >
              Sign in
            </a>
          </div>

          <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-3 text-[11px] text-stone-700 dark:text-stone-300">
            <span className="inline-flex items-center gap-1.5"><Shield size={13} className="text-orange-700 dark:text-orange-300" aria-hidden="true" /> LiveKit Cloud transport</span>
            <span className="inline-flex items-center gap-1.5"><Users size={13} className="text-orange-700 dark:text-orange-300" aria-hidden="true" /> Guest-friendly links</span>
            <span className="inline-flex items-center gap-1.5"><Sparkles size={13} className="text-orange-700 dark:text-orange-300" aria-hidden="true" /> Auto AI recap</span>
            <span className="inline-flex items-center gap-1.5"><Mail size={13} className="text-orange-700 dark:text-orange-300" aria-hidden="true" /> Digest email</span>
          </div>
        </div>
      </section>

      {/* ── Bullets grid ─────────────────────────────────────────── */}
      <section className="max-w-6xl mx-auto px-6 sm:px-10 py-14">
        <p className="text-[11px] font-black uppercase tracking-[0.3em] text-orange-700 dark:text-orange-300">Why HustleRoundup</p>
        <h2 className="mt-3 text-3xl sm:text-4xl font-black text-stone-900 dark:text-stone-50">
          A meeting room built into the Suite you already use.
        </h2>

        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <Bullet Icon={PlayCircle} iconTitle="Instant meetings"
            title="Instant meetings"
            body="One-tap new room. Share a `hustleroundup.au/m/aurora-wattle-482` link and anyone can join without a separate account." />
          <Bullet Icon={Sparkles} iconTitle="AI-generated recap"
            title="AI-generated recap"
            body="Transcript, action items and a 5-line digest email land in every attendee's inbox the moment the room closes." />
          <Bullet Icon={Video} iconTitle="Cloud recording"
            title="Cloud recording"
            body="LiveKit Egress records straight to R2 storage. Owners scrub, share and delete from the admin console." />
          <Bullet Icon={Users} iconTitle="Guest-friendly"
            title="Guest-friendly"
            body="Host toggles 'anyone with link can join' for cross-agency, contractor or customer meetings — one click." />
          <Bullet Icon={Shield} iconTitle="Host controls"
            title="Host controls"
            body="Waiting room, mute-all, kick, spotlight and lock-room controls keep every call tidy. Reactions and hand-raise on tap." />
          <Bullet Icon={Layers} iconTitle="One login, one platform"
            title="One login, one platform"
            body="Uses your existing HustleSuite account — no new sign-up flow, no separate password. Meeting history sits alongside your other Hustle activity." />
        </div>
      </section>

      {/* ── Big CTA ──────────────────────────────────────────────── */}
      <section className="max-w-6xl mx-auto px-6 sm:px-10 pb-24">
        <div className="rounded-3xl border border-orange-500/50 bg-gradient-to-br from-orange-500/10 to-purple-500/10 dark:from-orange-500/15 dark:to-purple-500/15 p-10 sm:p-14 text-center">
          <Zap size={30} className="mx-auto text-orange-700 dark:text-orange-300" strokeWidth={1.75} aria-label="Get started" title="Get started" />
          <h2 className="mt-4 text-3xl sm:text-4xl font-black text-stone-900 dark:text-stone-50">
            Your next call is one click away.
          </h2>
          <p className="mt-3 text-sm sm:text-base text-stone-700 dark:text-stone-300 max-w-xl mx-auto">
            Sign in with your HustleSuite account, spin up a room, drop the link
            in Slack. That&apos;s the whole thing.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <a
              href={APP_URL}
              data-testid="cta-bottom-start"
              rel="noopener"
              className="inline-flex items-center gap-2 rounded-full bg-orange-600 hover:bg-orange-700 text-white font-black px-7 h-12"
              title="Start a meeting on app.hustleroundup.au"
            >
              Start a meeting <ArrowRight size={16} aria-hidden="true" />
            </a>
            <a
              href={`${APP_URL}/signup`}
              data-testid="cta-bottom-join"
              rel="noopener"
              className="inline-flex items-center gap-2 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white font-black px-7 h-12"
              title="Join Hustle Membership — free"
            >
              Join Hustle Membership <ArrowRight size={16} aria-hidden="true" />
            </a>
          </div>
        </div>
      </section>

      <SuiteFooter />
    </div>
  );
}
